import { GraphQLClient, gql } from 'graphql-request';

import { getBlockHeight } from '@context/api/nekosdk';

type Volume = {
  id: string;
  volumeUSD: string;
};

type AllLiquidityPairsResponse = {
  volumeYesterday: Volume[];
  pairs: ILiquidityPair[];
};

type NativeTokenAttributes = {
  symbol: string;
  name: string;
  icon?: string;
};

type NativeTokenInfoType = Record<string, NativeTokenAttributes>;

const NATIVE_TOKEN_INFO: NativeTokenInfoType = {
  WBCH: {
    symbol: 'BCH',
    name: 'Smart BCH',
    icon: 'https://raw.githubusercontent.com/bitcoin-portal/cryptocurrency-icons/master/32%402x/icon/bch%402x.png',
  },
  WETH: {
    symbol: 'ETH',
    name: 'Ethereum',
    icon: 'https://icons.iconarchive.com/icons/cjdowner/cryptocurrency-flat/128/Ethereum-ETH-icon.png',
  },
};

const nativeTokenAttributes = (symbol: string): NativeTokenAttributes => {
  return NATIVE_TOKEN_INFO[symbol] || {};
};

const SBCH_LAST_BLOCK = 17652345;

export const getAllLiquidityPairs = async (
  url: string,
  exchangeProvider?: string,
): Promise<ILiquidityPair[]> => {
  try {
    const yesterdayStart = new Date();
    yesterdayStart.setUTCHours(0, 0, 0, 0);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const unixStart = Math.ceil(yesterdayStart.getTime() / 1000);

    const blockResp = await getBlockHeight(unixStart);
    let { height: previousDayBlock } = blockResp.getValue();

    if (exchangeProvider === 'bitcoincom') previousDayBlock = SBCH_LAST_BLOCK;

    const bcomClient = new GraphQLClient(url, {
      headers: {},
    });

    const pairsQuery = gql`
      query pairs($cursor: ID!) {
        pairs: pairs(
          first: 1000
          orderBy: id
          orderDirection: asc
          where: { id_gt: $cursor }
        ) {
          id
          name
          reserveUSD
          totalSupply
          volumeUSD
          token0 {
            name
            symbol
            id
            decimals
            derivedETH
          }
          token1 {
            name
            symbol
            id
            decimals
            derivedETH
          }
          token0Price
          token1Price
        }
      }
    `;

    const volumeYesterdayQuery = gql`
      query volumeYesterday {
        volumeYesterday: pairs(
          orderBy: reserveUSD
          orderDirection: desc
          block: { number: ${previousDayBlock} }
        ) {
          id
          volumeUSD
        }
      }
    `;

    // Include small and inactive pools so address-based withdrawal links work.
    const loadPairs = async (cursor = ''): Promise<ILiquidityPair[]> => {
      const { pairs: page } = await bcomClient.request<
        Pick<AllLiquidityPairsResponse, 'pairs'>
      >(pairsQuery, { cursor });
      if (page.length < 1000) return page;
      const next = page[page.length - 1].id;
      if (next <= cursor) throw new Error('Pool pagination did not advance');
      return [...page, ...(await loadPairs(next))];
    };
    const [pairs, volumeYesterday] = await Promise.all([
      loadPairs(),
      bcomClient
        .request<Pick<AllLiquidityPairsResponse, 'volumeYesterday'>>(
          volumeYesterdayQuery,
        )
        .then(res => res.volumeYesterday)
        // The subgraph indexer can lag behind chain head and reject queries
        // pinned to a block it hasn't reached yet. Degrade the 24h volume/APY
        // figures rather than failing the whole pairs list in that case.
        .catch(() => [] as Volume[]),
    ]);

    const reduced = pairs.map(pair => {
      const { reserveUSD, volumeUSD: volumeToNow } = pair;

      const { volumeUSD: volumeToYesterday } = volumeYesterday.find(
        i => i.id === pair.id,
      ) || { volumeUSD: volumeToNow };

      const volumeDay = Number(volumeToNow) - Number(volumeToYesterday);

      const estimatedApy =
        ((volumeDay * 0.0025 * 365) / Number(reserveUSD)) * 100;

      return {
        ...pair,
        token0: {
          ...pair.token0,
          ...nativeTokenAttributes(pair.token0.symbol),
        },
        token1: {
          ...pair.token1,
          ...nativeTokenAttributes(pair.token1.symbol),
        },
        apy: estimatedApy,
      };
    });

    return reduced.sort((a, b) => Number(b.reserveUSD) - Number(a.reserveUSD));
  } catch (error) {
    throw new Error(`Error getting all liquidity pairs: ${error}`);
  }
};
